import { makeAutoObservable } from 'mobx'
import { type Tag } from 'entities/Tags/model/Tag'
import { TagsDropdownControlStore } from './tagsDropdownControlStore'
import { TagsDropdownSearchStore } from './tagsDropdownSearchStore'
import {
  type ITagAction,
  type ITagErrorProps,
  type TagsDropdownStoreProps,
  type TagsDropdownVariant,
} from './types'

const DEFAULT_SHOW_LIMIT = 8

export class TagsDropdownStore {
  private _controlStore = new TagsDropdownControlStore()
  private _searchStore = new TagsDropdownSearchStore()
  private _variant: TagsDropdownVariant | null = null
  private _showLimit: number | null = null
  private _placeholder: string | null = null
  private _searchPlaceholder: string | null = null
  private _canCreate = false
  private _error: ITagErrorProps | null = null
  private _onChange: null | ((action: ITagAction) => void) = null
  private _hideTriggerLeftIcon = false
  private _showContactsCount = true

  constructor(props: TagsDropdownStoreProps) {
    makeAutoObservable(this)

    this._controlStore = props.controlStore
    this._variant = props.variant ?? null
    this._showLimit = props.showLimit ?? null
    this._placeholder = props.placeholder ?? null
    this._searchPlaceholder = props.searchPlaceholder ?? null
    this._canCreate = props.canCreate ?? false
    this._error = props.error ?? null
    this._onChange = props.onChange ?? null
    this._hideTriggerLeftIcon = props.hideTriggerLeftIcon ?? false
    this._showContactsCount = props.showContactsCount ?? true
  }

  get hideTriggerLeftIcon() {
    return this._hideTriggerLeftIcon
  }

  get showContactsCount() {
    return this._showContactsCount
  }

  get controlStore() {
    return this._controlStore
  }

  get searchStore() {
    return this._searchStore
  }

  get cantCreate() {
    return (
      !this._canCreate ||
      this.searchStore.loading ||
      this.searchStore.hasMore ||
      !this.searchStore.noResults ||
      !this.searchStore.term.trim() ||
      this.searchStore.tags.some((tag) => tag.label === this.searchStore.term) ||
      this.controlStore.tags.some((tag) => tag.label === this.searchStore.term)
    )
  }

  get error() {
    return this._error
  }

  get loading() {
    return this.searchStore.loading || this.controlStore.loading
  }

  get onChange() {
    return this._onChange
  }

  get showLimit() {
    return this._showLimit ? this._showLimit : DEFAULT_SHOW_LIMIT
  }

  get variant(): TagsDropdownVariant {
    return this._variant ? this._variant : 'select'
  }

  get placeholder() {
    return this._placeholder ? this._placeholder : 'Select tags'
  }

  get searchPlaceholder() {
    return this.controlStore.synced ? this._searchPlaceholder ?? 'Search tag' : 'Loading'
  }

  get previewTags() {
    return this._controlStore.tags.slice(0, this.showLimit)
  }

  get extraTagsCount() {
    if (this._controlStore.tagsCount > this.showLimit)
      return this._controlStore.tagsCount - this.showLimit
    return 0
  }

  get hasExtraTags() {
    return !!this.extraTagsCount
  }

  get isFormFieldVariant() {
    return this.variant === 'formField'
  }

  get searchTagsList() {
    return this.searchStore.tags.filter((tag) => !this.controlStore.tagsMap.has(tag.id))
  }

  get disabledSearch() {
    return this.controlStore.loading || !this.controlStore.synced
  }

  initSearchStore() {
    this.searchStore.searchTags('', this.controlStore.ids)
  }

  createTag = () => {
    this.controlStore
      .createTag(this.searchStore.term)
      .then((tag) => tag && this.onChange?.({ type: 'create', tag }))
      .finally(() => {
        this.searchStore.searchTags('', this.controlStore.ids)
      })
  }

  addTag = (tag: Tag) => {
    this.controlStore.addTag(tag)
    this.onChange?.({ type: 'add', tag })
    if (this.searchStore.term) this.searchStore.searchTags('', this.controlStore.ids)
  }

  removeTag = (tag: Tag) => {
    this.controlStore.removeTag(tag.id)
    this.onChange?.({ type: 'remove', tag })
  }
}
